/* © CNES */
import moment from 'moment';
import TileGrid from 'ol/tilegrid/TileGrid';
import { Feature } from '../../../models/Feature';
import { ItemFilter } from '../../../models/Filter';
import { CLOUD_COVER, END_DATE_TIME, START_DATE_TIME } from '../Constants';

/**
 * format Time Parameter.
 * @param isFiltersVisible is Filters View is visible.
 * @param filterData filterData context.
 * @param searchedFeatures searched Features to format Time from.
 * @returns string the formated Time Parameter for geoserver.
 */
export const formatTimeParameter = (
  isFiltersVisible: boolean,
  filterData: ItemFilter,
  searchedFeatures: Feature[]
) => {
  const dateFormat = 'YYYY-MM-DDTHH:mm:ss.SSS';
  let endDate: string;
  let startDate: string;

  if (isFiltersVisible) {
    let momentEndDate = filterData[END_DATE_TIME]
      ? moment(filterData[END_DATE_TIME]?.slice(0, -1))
      : moment('1960-01-01T00:00:00.000');
    let momentStartDate = filterData[START_DATE_TIME]
      ? moment(filterData[START_DATE_TIME]?.slice(0, -1))
      : moment();

    momentEndDate.milliseconds(0);
    momentStartDate.milliseconds(0);
    startDate = momentEndDate.format(dateFormat);
    endDate = momentStartDate.format(dateFormat);
  } else {
    endDate = searchedFeatures?.reduce((acc, curr) => {
      if (moment(curr.properties[END_DATE_TIME].slice(0, -1)).isAfter(acc)) {
        let momentDate = moment(curr.properties[END_DATE_TIME].slice(0, -1));
        acc = momentDate.format(dateFormat);
      }
      return acc;
    }, moment('1960-01-01T00:00:00.000').format(dateFormat));
    startDate = searchedFeatures?.reduce((acc, curr) => {
      if (moment(curr.properties[START_DATE_TIME].slice(0, -1)).isBefore(acc)) {
        let momentDate = moment(curr.properties[START_DATE_TIME].slice(0, -1));
        momentDate.milliseconds(0);
        acc = momentDate.format(dateFormat);
      }
      return acc;
    }, moment().format(dateFormat));
  }

  return moment(endDate).isSame(startDate)
    ? `${startDate}Z`
    : `${startDate}Z/${endDate}Z`;
};

/**
 * Format Cloud Cover Parameter for geoserver request.
 * @param filterData filterData context.
 * @returns string the formated Cloud Cover Parameter.
 */
export const formatCloudCoverParameter = (filterData: ItemFilter) => {
  let lowRangeValue = -1;
  let highRangeValue = 100;

  if (filterData[CLOUD_COVER] && typeof filterData[CLOUD_COVER] === 'object') {
    [lowRangeValue, highRangeValue] = filterData[CLOUD_COVER].map(
      (cloudValue) => parseInt(cloudValue) ?? -1
    );
  }

  return `${lowRangeValue}/${highRangeValue}`;
};

/**
 * Format Snow Cover Parameter for geoserver request.
 * @returns string the formatted Snow Cover Parameter.
 */
export const formatSnowCoverParameter = () => {
  let lowRangeValue = -1;
  let highRangeValue = 100;

  return `${lowRangeValue}/${highRangeValue}`;
};

const tileSourceGenerator = (
  baseUrl: string,
  tileSourceInfo: {
    sourceUrl: string;
    LAYERS: string;
    DIM_polarization: string | null;
    hasSnowCover: boolean;
    hasCloudCover: boolean;
  },
  formatTimeParameter: string,
  CQL_FILTERS: { name: string; CQLFilter: string }[],
  formatCloudCoverParameter: string,
  formatSnowCoverParameter: string,
  bbox: string | undefined,
  tileGrid: TileGrid
) => {
  return {
    url: `${baseUrl}${tileSourceInfo.sourceUrl}`,
    params: {
      VERSION: '1.1.0',
      LAYERS: tileSourceInfo.LAYERS,
      // styles: '',
      FORMAT: 'image/png',
      time: formatTimeParameter,
      ...(tileSourceInfo.DIM_polarization && {
        DIM_polarization: tileSourceInfo.DIM_polarization,
      }),
      ...(!!CQL_FILTERS.find((filter) => filter.name === tileSourceInfo.LAYERS)
        ?.CQLFilter.length && {
        CQL_FILTER: CQL_FILTERS.find(
          (filter) => filter.name === tileSourceInfo.LAYERS
        )?.CQLFilter,
      }),
      ...(tileSourceInfo.hasCloudCover && {
        DIM_cloudcover: formatCloudCoverParameter,
      }),
      ...(tileSourceInfo.hasSnowCover && {
        DIM_snowcover: formatSnowCoverParameter,
      }),
      ...(!!bbox && { BBOX: bbox }),
      maxFeatures: 10000,
    },
    serverType: 'geoserver',
    tileGrid,
  };
};

/**
 * tileSource Reference.
 */
export const tileSourceReference = [
  {
    sourceUrl: `/geoserver/peps/wms`,
    LAYERS: 'gdh_s1',
    DIM_polarization: 'DH,VV,HH,DV',
    hasSnowCover: false,
    hasCloudCover: false,
    CQLFilterName: 'CQLFilterS1',
  },
  {
    sourceUrl: `/geoserver/muscate/wms`,
    LAYERS: 'muscate:gdh_s2_l2a3a',
    DIM_polarization: null,
    hasSnowCover: false,
    hasCloudCover: true,
    CQLFilterName: 'CQLFilterSentinel2',
  },
  {
    sourceUrl: `/wms`,
    LAYERS: 'geodes:PEPS_S2_L1C',
    DIM_polarization: null,
    hasSnowCover: false,
    hasCloudCover: false,
    CQLFilterName: 'CQLFilterSentinel2',
  },
  {
    sourceUrl: `/geoserver/histo/wms`,
    LAYERS: 'histo:gdh_spot',
    DIM_polarization: null,
    hasSnowCover: true,
    hasCloudCover: true,
    CQLFilterName: 'CQLFilterSpot_reflectance',
  },
  {
    sourceUrl: `/geoserver/muscate/wms`,
    LAYERS: 'muscate:gdh_venus',
    DIM_polarization: null,
    hasSnowCover: false,
    hasCloudCover: true,
    CQLFilterName: 'CQLFilterVenus',
  },
  {
    sourceUrl: `/geoserver/histo/wms`,
    LAYERS: 'histo:gdh_spotdem',
    DIM_polarization: null,
    hasSnowCover: false,
    hasCloudCover: false,
    CQLFilterName: 'CQLFilterSpot_reflectance',
  },
  {
    sourceUrl: `/geoserver/histo/wms`,
    LAYERS: 'histo:gdh_landsat57',
    DIM_polarization: null,
    hasSnowCover: false,
    hasCloudCover: true,
    CQLFilterName: 'CQLFilterLandSat',
  },
  {
    sourceUrl: `/geoserver/muscate/wms`,
    LAYERS: 'muscate:gdh_landsat8',
    DIM_polarization: null,
    hasSnowCover: false,
    hasCloudCover: true,
    CQLFilterName: 'CQLFilterLandSat',
  },
  {
    sourceUrl: `/geoserver/muscate/wms`,
    LAYERS: 'muscate:gdh_l2bl3b',
    DIM_polarization: null,
    hasSnowCover: true,
    hasCloudCover: true,
    CQLFilterName: 'CQLFilterGdhSnow',
  },
  {
    sourceUrl: `/geoserver/muscate/wms`,
    LAYERS: 'muscate:gdh_oso',
    DIM_polarization: null,
    hasSnowCover: false,
    hasCloudCover: false,
    CQLFilterName: 'CQLFilterOSO',
  },
  {
    sourceUrl: `/geoserver/histo/wms`,
    LAYERS: 'histo:gdh_postel',
    DIM_polarization: null,
    hasSnowCover: false,
    hasCloudCover: false,
    CQLFilterName: 'CQLFilterPostel',
  },
  {
    sourceUrl: `/geoserver/trishna/wms`,
    LAYERS: 'trishna:gdh_trishna',
    DIM_polarization: null,
    hasSnowCover: false,
    hasCloudCover: false,
    CQLFilterName: 'CQLFilterTrishna',
  },
];

/**
 * generate TileSource
 * @param baseUrl url string for Tilesource.
 * @param formatTimeParameter function
 * @param CQL_FILTERS CQL_FILTERS
 * @param formatCloudCoverParameter function
 * @param formatSnowCoverParameter function
 * @param bbox bbox
 * @param tileGrid tileGrid
 * @returns TileSource
 */
export const generateTileSource = (
  baseUrl: string,
  formatTimeParameter: string,
  CQL_FILTERS: { name: string; CQLFilter: string; showLayer: boolean }[],
  formatCloudCoverParameter: string,
  formatSnowCoverParameter: string,
  bbox: string | undefined,
  tileGrid: TileGrid
) => {
  return tileSourceReference.map((sourceInfo) => {
    return tileSourceGenerator(
      baseUrl,
      sourceInfo,
      formatTimeParameter,
      CQL_FILTERS,
      formatCloudCoverParameter,
      formatSnowCoverParameter,
      bbox,
      tileGrid
    );
  });
};
